import type { ManualBankFormMode } from './manualBankSchema'

/**
 * Copy for the manual-bank screens on Financial Accounts settings, taken from
 * the prototype and kept in one place so screens and tests agree.
 */
export const MANUAL_BANK_COPY = {
  sectionTitle: 'Manual Financial Accounts',
  sectionSubtitle: 'Financial accounts added manually (not supported via LedgerSync integration)',
  addBankCta: 'Add Financial Account',
  emptyTitle: 'No manual financial accounts yet.',
  /** Empty list on a facility's page. */
  facilityEmptyTitle: 'No manual financial accounts yet for this facility.',
  emptyDescription: 'Click "Add Financial Account" to create your first financial account.',

  editBankCta: 'Edit Financial Account',
  deleteBankCta: 'Delete Financial Account',
  deleteBankDescription:
    'This will delete the institution and all its accounts and cards. Related transactions are kept. This action cannot be undone.',

  /** Detail page. */
  sourceBadge: 'Manual',
  metaPrefix: 'Manual financial account',
  notFound: 'This financial account no longer exists.',
  accountsEmpty: 'This financial account has no accounts or cards yet.',
  accountsEmptyFiltered: 'No accounts or cards match your search or filter.',
  noActiveFacilities:
    'No active facilities are available. Each account or card must be assigned to a facility — create or reactivate a facility in Settings → Facilities first.',
  addAccountCta: 'Add account or card',

  /** Bank form dialog. */
  addBankTitle: 'Add Financial Account',
  editBankTitle: 'Edit Financial Account',
  institutionLabel: 'Institution Name',
  institutionPlaceholder: 'e.g. Chase, Bank of America',
  accountsSectionLabel: 'Accounts & Cards',
  rowNameLabel: 'Account/ Card Name',
  rowNamePlaceholder: 'e.g. Operating Account',
  rowNumberLabel: 'Account/ Card #',
  numberPlaceholder: '123456789',
  typeLabel: 'Type',
  facilityLabel: 'Facility',
  facilityPlaceholder: 'Select facility',

  /** Single-account dialogs. */
  editAccountTitle: 'Edit account or card',
  editAccountNameLabel: 'Account/ card name',
  editAccountNamePlaceholder: 'e.g. First Colony Operating',
  editAccountNumberLabel: 'Account/ card number',
  deleteAccountDescription: 'This will be deleted permanently. Related transactions are kept.',
} as const

/** Title of the bank form in its "add accounts to an existing bank" mode. */
export function addAccountsTitle(bankName: string): string {
  return `Add account or card for ${bankName}`
}

/** Subtitle of the single-account edit dialog: `{bank} · manual`. */
export function editAccountSubtitle(bankName: string): string {
  return `${bankName} · manual`
}

/** Inline error for an institution name that another manual bank already uses. */
export function duplicateInstitutionMessage(name: string): string {
  return `A financial account named "${name}" already exists in your organization`
}

/**
 * Count of a bank's accounts for the list's "Accounts & cards" column and the
 * detail page's count badge, worded like the connections list: `1 account`,
 * `N accounts`.
 */
export function accountsCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'account' : 'accounts'}`
}

/** Title of the bank form for its mode. */
export function bankFormTitle(mode: ManualBankFormMode): string {
  switch (mode.kind) {
    case 'create':
      return MANUAL_BANK_COPY.addBankTitle
    case 'edit':
      return MANUAL_BANK_COPY.editBankTitle
    case 'add-accounts':
      return addAccountsTitle(mode.institutionName)
  }
}

/** The bank form's primary button for its mode. */
export function bankFormSubmitLabel(mode: ManualBankFormMode): string {
  switch (mode.kind) {
    case 'create':
      return 'Save'
    case 'edit':
      return 'Update'
    case 'add-accounts':
      return 'Add'
  }
}
